<template>
  <div
    class="transverse-slice"
    data-testid="oblique-single-slice"
    :data-slice-axis="slice.axis"
  >
    <header class="transverse-slice__header">
      <strong>{{ slice.title }}</strong>
      <span v-if="slice.axis === 'Axial'">
        Mueva el centro a la zona elegida y gire los extremos de la línea
        amarilla para orientar el corte transversal.
      </span>
      <span v-else>
        Corte de orientación manual. Ajuste su posición y ángulo desde la guía
        axial.
      </span>
    </header>
    <ObliqueSliceViewer
      :key="slice.axis"
      :view-id="slice.viewId"
      :outline-type="slice.outlineType"
      :view-direction="slice.direction"
      :view-up="slice.up"
    />
    <ViewOverlayGrid class="overlay-no-events view-annotations">
      <template #bottom-right>
        <div class="annotation-cell" @click.stop>
          <ViewTypeSwitcher :view-id="viewId" :image-id="currentImageID" />
        </div>
      </template>
    </ViewOverlayGrid>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ObliqueSliceViewer from '@/src/components/ObliqueSliceViewer.vue';
import ViewOverlayGrid from '@/src/components/ViewOverlayGrid.vue';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useViewStore } from '@/src/store/views';
import useResliceCursorStore from '@/src/store/reslice-cursor';
import type { LPSAxis, LPSAxisDir } from '@/src/types/lps';

const props = defineProps<{ viewId: string }>();
const viewStore = useViewStore();
const sliceDirections: Record<
  LPSAxis,
  { direction: LPSAxisDir; up: LPSAxisDir }
> = {
  Axial: { direction: 'Superior', up: 'Anterior' },
  Coronal: { direction: 'Posterior', up: 'Superior' },
  Sagittal: { direction: 'Right', up: 'Superior' },
};
const slice = computed(() => {
  const view = viewStore.getView(props.viewId);
  const axis =
    view?.type === 'Oblique' ? (view.options.slice ?? 'Sagittal') : 'Sagittal';
  return {
    axis,
    title: view?.name ?? 'Transversal',
    viewId: props.viewId + '-' + axis.toLowerCase(),
    outlineType: 'Oblique' + axis,
    ...sliceDirections[axis],
  };
});
const { currentImageID } = useCurrentImage();
useResliceCursorStore();
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
<style scoped>
.transverse-slice {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  flex: 1;
  background: black;
}
.transverse-slice__header {
  padding: 6px 12px;
  background: #0d202c;
  color: white;
  font-size: 0.75rem;
}
.transverse-slice__header strong {
  display: block;
}
.transverse-slice__header span {
  color: rgb(255 255 255 / 70%);
}
</style>
